import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import { readRefFromUrl, savePendingRef } from '@/utils/referral';

/** 欢迎页：创建 / 导入两个入口。 */
export function WelcomePage(): JSX.Element {
  const navigate = useNavigate();
  const status = useIdentityStore((s) => s.status);
  const setLoading = useIdentityStore((s) => s.setLoading);
  const pushError = useUiStore((s) => s.pushError);
  const [invitedBy, setInvitedBy] = useState<string | null>(null);

  useEffect(() => {
    // 捕获邀请链接 `?ref=` 并暂存（好友打开邀请链接时的落地）
    const ref = readRefFromUrl();
    if (ref) {
      savePendingRef(ref);
      setInvitedBy(ref);
    }
  }, []);

  useEffect(() => {
    // 已有身份时不该停在欢迎页
    let cancelled = false;
    void (async () => {
      try {
        setLoading(true);
        const has = await container.identity.hasIdentity();
        if (!cancelled && has) navigate('/unlock', { replace: true });
      } catch (err) {
        pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, pushError, setLoading]);

  return (
    <div className="flex min-h-full flex-col items-center justify-center gap-8 p-6">
      <div className="text-center">
        <h1 className="text-2xl font-semibold text-accent">MeshLink</h1>
        <p className="mt-2 text-sm text-dim">你的身份，只属于你自己的 12 个词</p>
      </div>

      <div className="w-[min(92vw,360px)] space-y-3">
        {invitedBy ? (
          <p className="rounded-btn border border-accent/30 bg-accent/10 px-3 py-2 text-center text-xs text-accent">
            🎁 你被好友邀请加入。创建身份并解锁后，在我的页可一键绑定邀请人，双方获得推荐奖励。
          </p>
        ) : null}
        <Button block size="lg" onClick={() => navigate('/create')} disabled={status === 'unlocked'}>
          创建新身份
        </Button>
        <Button block size="lg" variant="ghost" onClick={() => navigate('/import')}>
          导入已有身份
        </Button>
      </div>

      <p className="max-w-[320px] text-center text-xs leading-relaxed text-dim">
        身份创建全程在本机完成，不产生任何网络请求。助记词是找回账号的唯一方式，请务必抄写并离线保存。
      </p>
    </div>
  );
}

export default WelcomePage;
